import { lazy } from 'react';
import {
	AnyRoute,
	createRootRoute,
	createRoute,
	createRouter,
} from '@tanstack/react-router';

import { basepath } from '@/constants';
import config from '@/config';

import { logOut } from './auth/Auth.hooks';
import { callbackSearchSchema } from './callback/Callback.hooks';
import { issueSearchSchema } from '@/views/issue/Issue.hooks';
import { AUTH_KEY } from '@/views/home/auth/Authentication.hooks';

import { NotFound, RootRouteComponent } from '@/views/RootRoute';

// Lazy loaded routes
const LazyIssue = lazy(() => import('@/views/issue/Issue'));
const LazyDrafts = lazy(() => import('@/views/drafts/Drafts'));
const LazyHome = lazy(() => import('./home/Home'));
const LazyLogOut = lazy(() => import('./auth/Logout'));
const LazyAuth = lazy(() => import('./auth/Auth'));
const LazyCallback = lazy(() => import('./callback/Callback'));

const onClickLogout = localStorage.getItem(AUTH_KEY)
	? () => {
			logOut('/logout');
		}
	: undefined;

/* Routes declaration */

const rootRoute = createRootRoute({
	component: RootRouteComponent,
});

const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/',
	component: () => {
		return (
			<LazyAuth>
				<LazyHome onClickLogOut={onClickLogout} />
			</LazyAuth>
		);
	},
});

const issueRoute = createRoute({
	getParentRoute: () => rootRoute,
	component: () => {
		return (
			<LazyAuth>
				<LazyIssue onClickLogOut={onClickLogout} />
			</LazyAuth>
		);
	},
	validateSearch: issueSearchSchema,
	path: 'issues/$id',
});

const issueArticleRoute = createRoute({
	getParentRoute: () => issueRoute,
	component: () => {
		return (
			<LazyAuth>
				<LazyIssue onClickLogOut={onClickLogout} />
			</LazyAuth>
		);
	},
	validateSearch: issueSearchSchema,
	path: '$articleId',
});

const callbackRoute = createRoute({
	getParentRoute: () => rootRoute,
	component: LazyCallback,
	validateSearch: callbackSearchSchema,
	path: 'callback',
});

const logOutRoute = createRoute({
	getParentRoute: () => rootRoute,
	component: LazyLogOut,
	path: 'logout',
});

const draftsRoute = createRoute({
	getParentRoute: () => rootRoute,
	component: LazyDrafts,
	path: 'drafts',
});

/* END of routes declaration */

const routes: Array<AnyRoute> = [
	indexRoute,
	issueRoute,
	issueArticleRoute,
	callbackRoute,
	logOutRoute,
];

if (config?.app?.admin) {
	routes.push(draftsRoute);
}

const routeTree = rootRoute.addChildren(routes);

export const router = createRouter({
	routeTree,
	basepath,
	defaultNotFoundComponent: NotFound,
});
